import * as Api from '@ViewModels';
import moment from 'moment';
import { ISelectBoxOption } from '../../web/components/SelectBox';
import { ICreateCampaignRequest, IKeyDateSelection, TemplateOrTemplateFilter } from '../index';
import { getDefaultTimeStringValue } from './dateUtils';

/**
 * Calculates schedule campaign calendar date range
 * @source Original location: UiUtils.ts.backup line 114
 */
export const calcScheduleCampaignCalendarDateRange = ({
	selectedDate = new Date(),
	numWeeks = 4,
}: {
	selectedDate?: Date | moment.Moment;
	numWeeks?: number;
} = {}) => {
	const calendarStartDate = moment(selectedDate).startOf('month').startOf('week');
	const calendarEndDay = moment(calendarStartDate)
		.add(numWeeks - 1, 'weeks')
		.endOf('week');
	return { startDate: calendarStartDate.toDate(), endDate: calendarEndDay.toDate() };
};

/**
 * Gets schedule time options
 * @source Original location: UiUtils.ts.backup line 1283
 */
export const getScheduleTimeOptions = (
	minutes: number,
	userSession?: Api.UserSessionContext,
	sendEmailFeatures?: Partial<Api.ISendEmailFeatures>,
	selectedMoment?: moment.Moment,
	impersonationContext?: Api.IImpersonationContext,
	ignoreEmailIntervals = false
) => {
	let interval: Api.IInterval = null;
	const accountFeatures =
		impersonationContext?.account?.features?.sendEmail || userSession?.account?.features?.sendEmail;
	const sendEmail = sendEmailFeatures || accountFeatures;

	const checkMoment = moment(selectedMoment) || moment();

	if (ignoreEmailIntervals || !sendEmail?.observeSendIntervals) {
		interval = { endMinutes: 1440, startMinutes: 0 };
	} else {
		// isoWeekday returns 1-7 where 1 is Monday and 7 is Sunday
		switch (checkMoment.isoWeekday()) {
			case 1:
				interval = sendEmail?.useDiscreteIntervals ? sendEmail?.mondayInterval : sendEmail?.weekdaysInterval;
				break;
			case 2:
				interval = sendEmail?.useDiscreteIntervals ? sendEmail?.tuesdayInterval : sendEmail?.weekdaysInterval;
				break;
			case 3:
				interval = sendEmail?.useDiscreteIntervals ? sendEmail?.wednesdayInterval : sendEmail?.weekdaysInterval;
				break;
			case 4:
				interval = sendEmail?.useDiscreteIntervals ? sendEmail?.thursdayInterval : sendEmail?.weekdaysInterval;
				break;
			case 5:
				interval = sendEmail?.fridayInterval;
				break;
			case 6:
				interval = sendEmail?.saturdayInterval;
				break;
			case 7:
				interval = sendEmail?.sundayInterval;
				break;
			default:
				break;
		}
	}

	const ret: ISelectBoxOption<number>[] = [];
	if (interval) {
		// only replace if null or undefined - leave 0 intact
		interval.startMinutes = interval.startMinutes ?? 0;
		interval.endMinutes = interval.endMinutes ?? 1440;
		const min = minutesToHours(interval.startMinutes);
		// - 60 because if we are sending on the :05 minute, we don't want to allow
		// the user to send after that. If business hours end at 6:00, the last time
		// we should allow would be 5:05
		const max = minutesToHours(interval.endMinutes - 60);
		const selectedDate = new Date();

		for (let x = min; x <= max; x++) {
			selectedDate.setHours(x, minutes, 0);
			const title = getDefaultTimeStringValue(selectedDate);
			ret.push({
				isDefault: false,
				title,
				value: x,
			});
		}
	}

	return ret;
};

const minutesToHours = (min: number) => {
	return Math.floor(min / 60);
};

/**
 * Checks if schedule send overlaps with existing workload
 * @source Original location: UiUtils.ts.backup line 1354
 */
export const scheduleSendOverlapsWithExistingWorkload = (
	estimate?: Partial<Api.IEmailSendEstimate>,
	selectedDate?: Date,
	numberOfEmailsToSend?: number
) => {
	return !!selectedDate && !!estimate && !!estimate.emailWorkload
		? !!estimate.emailWorkload.forecast.find(x => {
				const sendStartMoment = moment(estimate.estimate.startDate).startOf('day');
				const sendEndMoment = moment(estimate.estimate.endDate).endOf('day');
				const numberOfDays = sendEndMoment.diff(sendStartMoment, 'days');
				const dateOverlaps = moment(x.date).startOf('day').isBetween(sendStartMoment, sendEndMoment, null, '[]');
				if (dateOverlaps) {
					if (numberOfDays > 0) {
						// send spans more than one 24hr period
						return true;
					}
					const dailyLimit =
						(estimate.emailWorkload.limits.find(limit => limit.intervalInMinutes === 1440) || {}).count || 0;
					return dailyLimit - x.count - (numberOfEmailsToSend || 1) < 0;
				}
				return false;
			})
		: false;
};

/**
 * Gets interval time options
 * @source Original location: UiUtils.ts.backup line 1465
 */
export const getIntervalTimeOptions = () => {
	const ret: ISelectBoxOption<number>[] = [];

	const selectedDate = new Date();
	for (let x = 0; x <= 23; x++) {
		for (let y = 0; y <= 3; y++) {
			const minutes = y * 15;
			selectedDate.setHours(x, minutes, 0);
			ret.push({
				title: getDefaultTimeStringValue(selectedDate),
				value: x * 60 + minutes,
			});
		}
	}

	return ret;
};

export const createKeyDateSelection = (
	keyDate: Api.IKeyDate,
	template?: TemplateOrTemplateFilter
): IKeyDateSelection => {
	return {
		contactSelection: {
			bulkFilterRequest: {
				contactFilterRequest: {
					criteria: [
						{
							property: Api.ContactFilterCriteriaProperty.UpcomingKeyDatesNotScheduled,
							value: keyDate?.kind,
						},
					],
				},
			},
		},
		kind: keyDate?.kind,
		month: keyDate?.month,
		template,
	};
};

export const getCreateCampaignRequestForKeyDate = (
	keyFact: Api.IKeyFact,
	template?: Api.ITemplate,
	request?: Omit<ICreateCampaignRequest, 'context' | 'type'>
): ICreateCampaignRequest<IKeyDateSelection> => {
	return {
		...(request || {}),
		context: createKeyDateSelection(keyFact?.keyDate, template),
		type: 'KeyDate',
	};
};

export const isCampaignScheduledToSend = (campaign: Api.ICampaign) => {
	return !campaign.cancelledDate && !campaign.completedDate && !!campaign.schedule;
};

export const hasCampaignStarted = (campaign: Api.ICampaign) =>
	isCampaignScheduledToSend(campaign) ? new Date(campaign.schedule.startDate) < new Date() : false;

export const campaignQueuedCount = (campaign: Api.ICampaign) =>
	campaign.totalEmails - campaign.sentSuccessfullyCount - campaign.failedCount - (campaign.noEmailAddressCount ?? 0);

export const isCampaignInFinalStatus = (campaign: Api.ICampaign) =>
	campaign.status === Api.EmailSendStatus.Complete ||
	campaign.status === Api.EmailSendStatus.Cancelled ||
	campaign.status === Api.EmailSendStatus.Rejected;

export const formatDateRangeString = (value?: Api.IRange<Date | null>): string => {
	if (!value?.start) {
		return '';
	}

	const f = 'MM/DD/YYYY';
	return `${moment(value.start).format(f)} - ${moment(value.end).format(f)}`;
};

export const createDraggableId = (stageId: string, opportunityId: string) => {
	return `${stageId}|${opportunityId}`;
};

export const parseDraggableId = (draggableId: string) => {
	const [stageId, opportunityId] = draggableId.split('|');
	return { stageId, opportunityId };
};
